/**
 * Enrichment Stage Drift — ops-admin view of org-wide stage state.
 *
 * Surfaces "did the last deploy create silent drift" at a glance:
 * per-stage counts of docs that are behind the current registered
 * version, with cost-to-cure totals. Mirrors what
 * `python scripts/fetch_stages.py --drift` shows in the CLI.
 *
 * Data source: GET /api/v1/ops/stages/drift?sample_size=...
 * (read-only — actual backfill is dispatched per-case from the
 * Document Inventory tab.)
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';

const SAMPLE_SIZES = [500, 2000, 10000];

function formatDollars(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

function StatTile({ label, value, sub }) {
  return (
    <div className="info-item">
      <label>{label}</label>
      <span>{value}</span>
      {sub && <small className="text-muted" style={{ display: 'block', fontSize: 11 }}>{sub}</small>}
    </div>
  );
}

export default function StageDrift() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sampleSize, setSampleSize] = useState(2000);

  const fetchDrift = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get(`/ops/stages/drift?sample_size=${sampleSize}`);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load drift');
    } finally {
      setLoading(false);
    }
  }, [sampleSize]);

  useEffect(() => { fetchDrift(); }, [fetchDrift]);

  const versions = data?.registry_versions || {};
  const byStage = data?.by_stage || {};
  // Show every registered stage, even if 0 pending — drift to zero is
  // a useful "green light" signal, not silence.
  const stageNames = Object.keys(versions).sort();

  return (
    <div className="ops-stage-drift">
      <div className="ops-page-header">
        <h1>Enrichment Drift</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Sample size:&nbsp;
            <select
              value={sampleSize}
              onChange={(e) => setSampleSize(Number(e.target.value))}
              className="form-select"
              style={{ padding: '4px 8px' }}
            >
              {SAMPLE_SIZES.map(n => (
                <option key={n} value={n}>{n.toLocaleString()}</option>
              ))}
            </select>
          </label>
          <button className="btn btn-sm btn-ghost" onClick={fetchDrift} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: -8 }}>
        How many of the most recently updated docs are behind the current
        enrichment-stage registry. Backfill is dispatched per-case from a
        case's Document Inventory tab; this view is read-only.
      </p>

      {error && (
        <div className="ops-section" style={{ background: 'rgba(220, 38, 38, 0.05)' }}>
          <strong style={{ color: '#dc2626' }}>Error:</strong> {error}
        </div>
      )}

      {data && (
        <>
          <div className="ops-section">
            <h2>Snapshot</h2>
            <div className="info-grid">
              <StatTile
                label="Registered stages"
                value={stageNames.length}
              />
              <StatTile
                label="Docs scanned"
                value={(data.docs_total || 0).toLocaleString()}
                sub={`sample of ${sampleSize.toLocaleString()}`}
              />
              <StatTile
                label="Docs with pending"
                value={(data.docs_with_pending || 0).toLocaleString()}
                sub={`${
                  data.docs_total
                    ? Math.round(100 * (data.docs_with_pending || 0) / data.docs_total)
                    : 0
                }% of sample`}
              />
              <StatTile
                label="Cost-to-cure"
                value={formatDollars(data.total_cost_cents)}
                sub={`free ${formatDollars(data.free_cost_cents)} · paid ${formatDollars(data.paid_cost_cents)}`}
              />
            </div>
          </div>

          <div className="ops-section">
            <h2>Per-stage drift</h2>
            {stageNames.length === 0 ? (
              <p className="text-muted">No stages registered.</p>
            ) : (
              <table className="ops-table">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th>Current ver</th>
                    <th>Cost class</th>
                    <th style={{ textAlign: 'right' }}>Docs behind</th>
                    <th style={{ textAlign: 'right' }}>Cost-to-cure</th>
                  </tr>
                </thead>
                <tbody>
                  {stageNames.map(name => {
                    const cur = versions[name];
                    const info = byStage[name];
                    const docsBehind = info?.docs_missing || 0;
                    const cost = info?.cost_to_cure_cents || 0;
                    const costClass = info?.cost_class || '—';
                    return (
                      <tr key={name}>
                        <td><code>{name}</code></td>
                        <td>v{cur}</td>
                        <td>
                          <span className={`badge badge-${costClass === 'analyze' ? 'suspended' : 'active'}`}>
                            {costClass}
                          </span>
                        </td>
                        <td style={{
                          textAlign: 'right',
                          fontWeight: docsBehind > 0 ? 600 : 400,
                          color: docsBehind > 0 ? 'var(--text)' : 'var(--text-muted)',
                        }}>
                          {docsBehind.toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right', color: cost > 0 ? 'var(--text)' : 'var(--text-muted)' }}>
                          {formatDollars(cost)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!data && loading && (
        <p className="loading-text">Loading drift snapshot…</p>
      )}
    </div>
  );
}
